import { z } from 'zod';

import { ApiError } from '../api/client';
import {
  BILLING_CYCLES,
  type BillingCycle,
  type ContractFormInput,
  type ContractUpdateInput,
} from './api';

const cutoffTimeSchema = z
  .string()
  .trim()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, { message: 'Formato esperado HH:MM' });

const nonNegativeIntString = z
  .string()
  .trim()
  .refine((value) => value === '' || (/^\d+$/.test(value) && Number.isInteger(Number(value))), {
    message: 'Ingresá un número entero no negativo',
  });

/**
 * Mirrors `apps/api` `createContractSchema`/`updateContractSchema`
 * (`contract.dto.ts`), including the two cross-field checks
 * (`contracts/spec.md` → "Coherencia de vigencia", "Coherencia de topes de
 * cantidad diaria") so the form rejects them inline before ever reaching
 * the server, same criterion `customerFormSchema` already applies to its
 * own cross-field address check.
 */
export const contractFormSchema = z
  .object({
    code: z.string().trim().min(1, 'El código es obligatorio').max(100),
    customerId: z.string().uuid({ message: 'Seleccioná un cliente' }),
    operatingUnitId: z.string().uuid({ message: 'Seleccioná un punto de venta' }),
    startDate: z.string().trim().min(1, 'La fecha de inicio es obligatoria'),
    endDate: z.string().trim(),
    billingCycle: z.enum(BILLING_CYCLES, { message: 'Seleccioná un ciclo de facturación' }),
    paymentTermDays: nonNegativeIntString.refine((value) => value !== '', {
      message: 'El plazo de pago es obligatorio',
    }),
    cutoffTime: z.string().trim(),
    cutoffDaysBefore: nonNegativeIntString,
    minDailyQuantity: nonNegativeIntString,
    maxDailyQuantity: nonNegativeIntString,
    allowsCutoffOverride: z.boolean(),
    serviceDays: z.array(z.number().int().min(1).max(7)),
    includesHolidays: z.boolean(),
    notes: z.string().trim().max(2000),
  })
  .superRefine((data, ctx) => {
    if (data.cutoffTime !== '' && !cutoffTimeSchema.safeParse(data.cutoffTime).success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Formato esperado HH:MM',
        path: ['cutoffTime'],
      });
    }

    if (data.endDate !== '' && data.startDate !== '' && data.endDate < data.startDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'La fecha de fin debe ser posterior a la fecha de inicio',
        path: ['endDate'],
      });
    }

    if (data.minDailyQuantity !== '' && data.maxDailyQuantity !== '') {
      if (Number(data.minDailyQuantity) > Number(data.maxDailyQuantity)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'El mínimo no puede ser mayor al máximo',
          path: ['maxDailyQuantity'],
        });
      }
    }
  });

/** Raw, unvalidated shape bound to the inputs (post-resolver output is the same shape here: no transform). */
export type ContractFormValues = z.infer<typeof contractFormSchema>;

export const DEFAULT_FORM_VALUES: ContractFormValues = {
  code: '',
  customerId: '',
  operatingUnitId: '',
  startDate: '',
  endDate: '',
  billingCycle: '' as BillingCycle,
  paymentTermDays: '',
  cutoffTime: '',
  cutoffDaysBefore: '',
  minDailyQuantity: '',
  maxDailyQuantity: '',
  allowsCutoffOverride: false,
  serviceDays: [],
  includesHolidays: false,
  notes: '',
};

export function toCreateInput(values: ContractFormValues): ContractFormInput {
  return {
    code: values.code,
    customerId: values.customerId,
    operatingUnitId: values.operatingUnitId,
    startDate: values.startDate,
    billingCycle: values.billingCycle,
    paymentTermDays: Number(values.paymentTermDays),
    ...(values.endDate === '' ? {} : { endDate: values.endDate }),
    ...(values.cutoffTime === '' ? {} : { cutoffTime: values.cutoffTime }),
    ...(values.cutoffDaysBefore === ''
      ? {}
      : { cutoffDaysBefore: Number(values.cutoffDaysBefore) }),
    ...(values.minDailyQuantity === ''
      ? {}
      : { minDailyQuantity: Number(values.minDailyQuantity) }),
    ...(values.maxDailyQuantity === ''
      ? {}
      : { maxDailyQuantity: Number(values.maxDailyQuantity) }),
    allowsCutoffOverride: values.allowsCutoffOverride,
    serviceDays: values.serviceDays,
    includesHolidays: values.includesHolidays,
    ...(values.notes === '' ? {} : { notes: values.notes }),
  };
}

/**
 * A PATCH sends only the fields that actually changed, same convention as
 * `CustomerDataTab`'s `diffChanges`. `effectiveDate` is never diffed: it is
 * not a stored field, only a hint the caller attaches when the edit targets
 * an `ACTIVE` contract (`contracts/spec.md` → "Edición de contrato activo
 * genera nueva versión").
 */
export function diffChanges(
  current: {
    operatingUnitId: string;
    billingCycle: BillingCycle;
    paymentTermDays: number;
    cutoffTime: string | null;
    cutoffDaysBefore: number | null;
    minDailyQuantity: number | null;
    maxDailyQuantity: number | null;
    allowsCutoffOverride: boolean;
    serviceDays: number[];
    includesHolidays: boolean;
    notes: string | null;
    endDate: string | null;
  },
  values: ContractFormValues,
): ContractUpdateInput {
  const changes: ContractUpdateInput = {};

  if (values.operatingUnitId !== current.operatingUnitId) {
    changes.operatingUnitId = values.operatingUnitId;
  }

  if (values.billingCycle !== current.billingCycle) changes.billingCycle = values.billingCycle;

  const newPaymentTermDays = Number(values.paymentTermDays);
  if (newPaymentTermDays !== current.paymentTermDays) changes.paymentTermDays = newPaymentTermDays;

  const newCutoffTime = values.cutoffTime === '' ? null : values.cutoffTime;
  if (newCutoffTime !== current.cutoffTime) changes.cutoffTime = newCutoffTime;

  const newCutoffDaysBefore =
    values.cutoffDaysBefore === '' ? null : Number(values.cutoffDaysBefore);
  if (newCutoffDaysBefore !== current.cutoffDaysBefore)
    changes.cutoffDaysBefore = newCutoffDaysBefore;

  const newMinDailyQuantity =
    values.minDailyQuantity === '' ? null : Number(values.minDailyQuantity);
  if (newMinDailyQuantity !== current.minDailyQuantity)
    changes.minDailyQuantity = newMinDailyQuantity;

  const newMaxDailyQuantity =
    values.maxDailyQuantity === '' ? null : Number(values.maxDailyQuantity);
  if (newMaxDailyQuantity !== current.maxDailyQuantity)
    changes.maxDailyQuantity = newMaxDailyQuantity;

  if (values.allowsCutoffOverride !== current.allowsCutoffOverride) {
    changes.allowsCutoffOverride = values.allowsCutoffOverride;
  }

  if (
    JSON.stringify([...values.serviceDays].sort()) !==
    JSON.stringify([...current.serviceDays].sort())
  ) {
    changes.serviceDays = values.serviceDays;
  }

  if (values.includesHolidays !== current.includesHolidays)
    changes.includesHolidays = values.includesHolidays;

  const newNotes = values.notes === '' ? null : values.notes;
  if (newNotes !== current.notes) changes.notes = newNotes;

  const newEndDate = values.endDate === '' ? null : values.endDate;
  if (newEndDate !== current.endDate) changes.endDate = newEndDate;

  return changes;
}

export type FieldErrorTarget = 'code' | 'endDate' | 'minDailyQuantity' | 'maxDailyQuantity';

/**
 * Server errors never have their `message` parsed: the `code` field on
 * 409/422 bodies (`ContractsExceptionFilter`) drives the mapping, same
 * criterion `mapCreateError`/`mapSiteError` already apply.
 */
export function mapContractError(error: unknown): {
  formError?: string;
  fieldError?: { field: FieldErrorTarget; message: string };
} {
  if (error === undefined || error === null) {
    return {};
  }

  if (!(error instanceof ApiError)) {
    return { formError: 'Ocurrió un error de conexión. Intentá de nuevo.' };
  }

  const code = (error.body as { code?: string } | null)?.code;

  if (error.status === 409 && code === 'CONTRACT_CODE_DUPLICATE') {
    return {
      fieldError: { field: 'code', message: 'Ya existe un contrato en curso con ese código' },
    };
  }

  if (error.status === 422 && code === 'INVALID_DATE_RANGE') {
    return {
      fieldError: {
        field: 'endDate',
        message: 'La fecha de fin debe ser posterior a la fecha de inicio',
      },
    };
  }

  if (error.status === 422 && code === 'INVALID_QUANTITY_RANGE') {
    return {
      fieldError: { field: 'maxDailyQuantity', message: 'El mínimo no puede ser mayor al máximo' },
    };
  }

  if (error.status === 409 && code === 'CONTRACT_UNIT_PERIOD_OVERLAP') {
    return {
      formError:
        'Ya hay un contrato vigente de esta empresa en este punto de venta en esas fechas.',
    };
  }

  if (error.status === 409 && code === 'CONTRACT_OPERATING_UNIT_IMMUTABLE') {
    return { formError: 'El punto de venta de un contrato no se puede cambiar una vez creado.' };
  }

  if (error.status === 409 && code === 'CONTRACT_VERSION_LOCKED') {
    return { formError: 'Esta versión del contrato ya está cerrada y no admite cambios.' };
  }

  if (error.status === 403) {
    return { formError: 'No tenés permiso para realizar esta acción.' };
  }

  if (error.status === 400) {
    return { formError: 'Revisá los datos ingresados e intentá de nuevo.' };
  }

  return { formError: 'Ocurrió un error inesperado. Intentá de nuevo.' };
}
